import {
  BookkeepersManagementBulkUpdateIncidenceStatusDocument,
  IncidenceStatusResultFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { IncidenceQueryData } from 'modules/bookkeepers/hooks/IncidencesContext'
import { trackIncidenceChangeStatusBulk } from 'modules/bookkeepers/lib/amplitudeData'

import {
  ElectiveIncidenceStatus,
  inhouseStatusCopies,
  professionalsStatusCopies,
} from '../lib/Incidences/Status'
import { useInhousePayroll } from './useInhousePayroll'

interface UseBulkIncidenceStatusChangeParams {
  onStatusChanged: (updatedIncidences: IncidenceStatusResultFragment[]) => void
}

const bulkActionType = (status: ElectiveIncidenceStatus) => {
  switch (status) {
    case 'in-preparation':
      return 'send'
    case 'discarded':
      return 'discard'
    case 'doing':
      return 'doing'
    case 'to-do':
      return 'to-do'
    case 'done':
      return 'done'
  }
}

const useBulkIncidenceStatusChange = ({ onStatusChanged }: UseBulkIncidenceStatusChangeParams) => {
  const { hasInhousePayroll } = useInhousePayroll()
  const [bulkUpdateIncidenceStatus, { loading: updatingStatusBulk }] = useMutation(
    BookkeepersManagementBulkUpdateIncidenceStatusDocument,
    {
      extractorFn: (data) =>
        data.bookkeepersManagementMutations.bulkUpdateBookkeepersmanagementIncidence,
    }
  )

  const bulkChangeIncidenceStatuses = async (
    selectedIncidences: IncidenceQueryData[],
    newStatus: ElectiveIncidenceStatus,
    ignoreWarnings?: boolean
  ) => {
    /**
     * Update the incidences that successfully changed status remotely
     * @param updatedIncidences the network response with the updated incidences
     * @returns incidences that were updated locally
     */

    const updateChangedIncidences = (updatedIncidences: IncidenceStatusResultFragment[]) => {
      const incidencesMap = new Map(
        selectedIncidences.map((incidence) => [incidence.id, incidence])
      )

      const changedIncidences = updatedIncidences.filter((updatedIncidence) => {
        const selectedIncidence = incidencesMap.get(updatedIncidence.id)
        if (selectedIncidence && selectedIncidence.status !== updatedIncidence.status) {
          // This is writing a MOBX model
          selectedIncidence.status = updatedIncidence.status
          return true
        }
        return false
      })

      return changedIncidences
    }

    const incidencesToChange = selectedIncidences.filter((i) => i.status !== newStatus)
    const incidenceIds = incidencesToChange.map((i) => i.id)

    return bulkUpdateIncidenceStatus({
      variables: {
        incidenceIds,
        newIncidenceStatus: newStatus,
        ignoreWarnings: ignoreWarnings,
      },
    }).then((response) => {
      const result = handleResult(response, () => undefined)
      if (result.success) {
        const resultingIncidences = result.data.incidences || []
        const changedIncidences = updateChangedIncidences(resultingIncidences)

        app.setFlash({
          type: 'correct',
          message: i18n.t('incidences.quick_actions.success', {
            incidencesMoved: changedIncidences.length,
            status:
              hasInhousePayroll && newStatus !== 'in-preparation'
                ? inhouseStatusCopies()[newStatus]
                : professionalsStatusCopies(false)[newStatus],
          }),
        })

        // Notify the caller of the incidences to update
        onStatusChanged(changedIncidences)

        // Track the update
        trackIncidenceChangeStatusBulk(bulkActionType(newStatus), changedIncidences.length, false)
      }
      return result
    })
  }

  return { bulkChangeIncidenceStatuses, updatingStatusBulk }
}

export default useBulkIncidenceStatusChange
